"use client";

import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FaTelegram } from "react-icons/fa";

interface ErrorStateProps {
  title?: string;
  description?: string;
  errorMessage?: string;
  actionText?: string;
  showSupportLink?: boolean;
  supportInfo?: string;
  onAction?: () => void;
  className?: string;
}

export function ErrorState({
  title = "Error Occurred",
  description = "We encountered an issue while processing your request.",
  errorMessage,
  actionText = "Try Again",
  showSupportLink = true,
  supportInfo,
  onAction,
  className = "",
}: ErrorStateProps) {
  
  const openTelegramSupport = () => {
    window.open('https://t.me/batchfinderdev', '_blank');
  };

  return (
    <div className={`w-full rounded-lg border border-destructive/20 bg-destructive/5 p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
        <div className="space-y-2 w-full">
          <h4 className="font-medium text-destructive">{title}</h4>
          <p className="text-sm text-muted-foreground">{description}</p>
          
          {errorMessage && (
            <div className="mt-2 p-2 bg-destructive/10 text-destructive rounded-md text-sm">
              {errorMessage}
            </div>
          )}
          
          {showSupportLink && (
            <div className="mt-4 space-y-2">
              <p className="text-xs text-muted-foreground">If the problem persists, please contact our support team:</p>
              <Button 
                variant="outline" 
                size="sm"
                className="w-full flex items-center justify-center gap-2 text-xs"
                onClick={openTelegramSupport}
              >
                <FaTelegram className="h-3 w-3 text-[#229ED9]" />
                Contact Support
              </Button>
              
              {supportInfo && (
                <p className="text-xs text-muted-foreground mt-2">
                  {supportInfo}
                </p>
              )}
            </div>
          )}
          
          {onAction && (
            <Button 
              className="mt-3 w-full bg-destructive hover:bg-destructive/90 text-sm"
              size="sm"
              onClick={onAction}
            >
              {actionText}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
} 